import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notebooksApi } from '@/lib/api/notebooks'
import { QUERY_KEYS } from '@/lib/api/query-client'
import { useToast } from '@/lib/hooks/use-toast'
import { CreateNotebookRequest, UpdateNotebookRequest } from '@/lib/types/api'
import { useI18n } from '@/lib/hooks/use-i18n'

export function useNotebooks(archived?: boolean) {
  return useQuery({
    queryKey: [...QUERY_KEYS.notebooks, { archived }],
    queryFn: () => notebooksApi.list({ archived, order_by: 'updated desc' }),
  })
}

export function useNotebook(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.notebook(id),
    queryFn: () => notebooksApi.get(id),
    enabled: !!id,
  })
}

export function useCreateNotebook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: (data: CreateNotebookRequest) => notebooksApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notebooks })
      toast({
        title: t('notebooks.toast.create.success.title'),
        description: t('notebooks.toast.create.success.description'),
      })
    },
    onError: () => {
      toast({
        title: t('notebooks.toast.create.error.title'),
        description: t('notebooks.toast.create.error.description'),
        variant: 'destructive',
      })
    },
  })
}

export function useUpdateNotebook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNotebookRequest }) =>
      notebooksApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notebooks })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notebook(id) })
      toast({
        title: t('notebooks.toast.update.success.title'),
        description: t('notebooks.toast.update.success.description'),
      })
    },
    onError: () => {
      toast({
        title: t('notebooks.toast.update.error.title'),
        description: t('notebooks.toast.update.error.description'),
        variant: 'destructive',
      })
    },
  })
}

export function useDeleteNotebook() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { t } = useI18n()

  return useMutation({
    mutationFn: (id: string) => notebooksApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notebooks })
      toast({
        title: t('notebooks.toast.delete.success.title'),
        description: t('notebooks.toast.delete.success.description'),
      })
    },
    onError: () => {
      toast({
        title: t('notebooks.toast.delete.error.title'),
        description: t('notebooks.toast.delete.error.description'),
        variant: 'destructive',
      })
    },
  })
}
